import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import UploadIcon from '@mui/icons-material/Upload';
import { LoadingButton } from '@mui/lab';
import { TextField } from '@mui/material';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { mediaService } from '../../api';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { holdingCompanyFormFields } from '../../forms/fields/formFields';
import { holdingCompanyFormSchema } from '../../forms/validationSchema/formSchema';
import { useAppDispatch } from '../../redux/hooks';
import { createHoldingCompany, updateHoldingCompany } from '../../redux/modules/holdingCompany/holdingCompany.actions';
import { holdingCompanySelector } from '../../redux/modules/holdingCompany/holdingCompany.selectors';
import { HoldingCompany } from '../../types/holdingCompany';

interface HoldingCompanyDialogProps {
  open: boolean;
  initialValue: HoldingCompany | null;
  onClose: (isReload: boolean) => void;
}

export function HoldingCompanyDialog({ open, initialValue, onClose }: HoldingCompanyDialogProps) {
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { handleSubmit, values, handleChange, touched, errors, submitForm, setValues, setFieldValue, resetForm } =
    useFormik({
      initialValues: {
        [holdingCompanyFormFields.ticker]: '',
        [holdingCompanyFormFields.nameEn]: '',
        [holdingCompanyFormFields.nameAr]: '',
        [holdingCompanyFormFields.website]: '',
        [holdingCompanyFormFields.logoUrl]: '',
      },
      validationSchema: holdingCompanyFormSchema,
      onSubmit: (formValues) => {
        // Empty optional fields must reach the API as null, not '' — the backend documents
        // logoUrl: null as "render the placeholder", and nameAr: null as "not yet translated".
        // An empty string satisfies neither contract.
        const payload = Object.fromEntries(
          Object.entries(formValues).map(([key, value]) => [key, value === '' ? null : value]),
        ) as typeof formValues;

        if (initialValue?.id) {
          dispatch(updateHoldingCompany({ id: initialValue.id, ...payload }));
        } else {
          dispatch(createHoldingCompany(payload));
        }
      },
    });
  const { createStatus, updateStatus, error } = useSelector(holdingCompanySelector);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const logoUrl = values[holdingCompanyFormFields.logoUrl] ?? '';

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    setFieldValue(holdingCompanyFormFields.logoUrl, '');
    try {
      const response = await mediaService.uploadAnonymousPublic(file, 'company');
      if (response?.data?.url) {
        setFieldValue(holdingCompanyFormFields.logoUrl, response.data.url);
      }
    } catch (err) {
      console.error('Logo upload error:', err);
    } finally {
      setLogoUploading(false);
    }
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleClearLogo = () => {
    setFieldValue(holdingCompanyFormFields.logoUrl, '');
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const logoSize = 80;

  useEffect(() => {
    if (!open) {
      resetForm();
      setLogoUploading(false);
    }
  }, [open]);

  useEffect(() => {
    const url = initialValue?.logoUrl ?? '';
    setValues({
      ticker: initialValue?.ticker || '',
      nameEn: initialValue?.nameEn || '',
      nameAr: initialValue?.nameAr || '',
      website: initialValue?.website || '',
      [holdingCompanyFormFields.logoUrl]: url,
    });
  }, [initialValue]);

  useEffect(() => {
    if (createStatus === fetchStatuses.success) {
      onClose(true);
      enqueueSnackbar('Company created!', { variant: 'success' });
    }
    if (createStatus === fetchStatuses.rejected) {
      enqueueSnackbar('Company creation error!', { variant: 'error' });
    }

    if (updateStatus === fetchStatuses.success) {
      onClose(true);
      enqueueSnackbar('Company updated!', { variant: 'success' });
    }
  }, [createStatus, updateStatus]);

  return (
    <Dialog open={open} fullWidth>
      <DialogTitle>{initialValue?.id ? 'Edit Company' : 'Create a new Company'}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Box sx={{ mt: 1, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              style={{ display: 'none' }}
              id="holding-company-logo"
            />
            <label htmlFor="holding-company-logo" style={{ cursor: logoUploading ? 'wait' : 'pointer' }}>
              <Box
                component="span"
                sx={{
                  position: 'relative',
                  width: logoSize,
                  height: logoSize,
                  borderRadius: '50%',
                  border: '2px dashed',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  bgcolor: logoUrl ? 'transparent' : 'action.hover',
                  pointerEvents: logoUploading ? 'none' : 'auto',
                  '&:hover': logoUploading ? {} : { borderColor: 'primary.main', bgcolor: 'action.selected' },
                }}
              >
                {logoUploading && <CircularProgress size={36} />}
                {logoUrl && !logoUploading && (
                  <Avatar src={logoUrl} alt="Logo" sx={{ width: logoSize, height: logoSize }} />
                )}
                {!logoUrl && !logoUploading && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                    <UploadIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Select logo
                    </Typography>
                  </Box>
                )}
              </Box>
            </label>
            {logoUploading && (
              <Typography variant="body2" color="text.secondary">
                Uploading…
              </Typography>
            )}
            {logoUrl && !logoUploading && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Logo uploaded
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleClearLogo}
                >
                  Clear
                </Button>
              </>
            )}
          </Box>
          <DialogContentText>
            {error && <Alert severity="error">{error.message}</Alert>}
            Enter the Company details
          </DialogContentText>
          <TextField
            margin="normal"
            required
            fullWidth
            id="nameEn"
            name={holdingCompanyFormFields.nameEn}
            label="Name En"
            value={values[holdingCompanyFormFields.nameEn]}
            onChange={handleChange}
            error={touched.nameEn && !!errors.nameEn}
            helperText={touched.nameEn && errors.nameEn}
          />
          <TextField
            dir="rtl"
            margin="normal"
            fullWidth
            id="nameAr"
            name={holdingCompanyFormFields.nameAr}
            label="Name Ar"
            value={values[holdingCompanyFormFields.nameAr]}
            onChange={handleChange}
            error={touched.nameAr && !!errors.nameAr}
            helperText={touched.nameAr && errors.nameAr}
          />
          <TextField
            margin="normal"
            fullWidth
            id={holdingCompanyFormFields.ticker}
            name={holdingCompanyFormFields.ticker}
            label="Ticker"
            value={values[holdingCompanyFormFields.ticker]}
            onChange={handleChange}
            error={touched.ticker && !!errors.ticker}
            helperText={touched.ticker && errors.ticker}
          />
          <TextField
            margin="normal"
            fullWidth
            id={holdingCompanyFormFields.website}
            name={holdingCompanyFormFields.website}
            label="Website"
            value={values[holdingCompanyFormFields.website]}
            onChange={handleChange}
            error={touched.website && !!errors.website}
            helperText={touched.website && errors.website}
          />
        </form>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          type="button"
          color="primary"
          variant="contained"
          endIcon={<SaveIcon />}
          loading={createStatus === fetchStatuses.pending}
          loadingPosition="end"
          onClick={submitForm}
        >
          {initialValue?.id ? 'Save' : 'Create'}
        </LoadingButton>
        <Button color="inherit" variant="outlined" onClick={() => onClose(false)}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
